import { API_ENDPOINTS } from "./apiEndpoints";
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from "react-router-dom";
import ShopifyConverter from "./ShopifyConverter";
import CheckInventory from "./CheckInventory";
import VariantInventoryGenerator from './VariantInventory';
import PreprocessFile from "./PreprocessFile";
import Login from "./Login";
import { checkAuth, clearToken, API_BASE } from "./api";
import "./ShopifyConverter.css";
import "./Login.css";
import "./App.css";

import ShopifySkuMatch from "./ShopifySkuMatch";
import SkeletonLoader from "./SkeletonLoader";
import Settings from "./Settings";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");

  const verifyUserAuth = async () => {
    try {
      const isAuth = await checkAuth();
      setIsAuthenticated(isAuth);
      if (isAuth) {
        // Fetch user profile info
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}${API_ENDPOINTS.me}`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const userData = await res.json();
          setUsername(userData.username);
        }
      }
    } catch (err) {
      console.error(err);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyUserAuth();

    const handleUnauthorized = () => {
      clearToken();
      setIsAuthenticated(false);
      setUsername("");
    };

    window.addEventListener("unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    verifyUserAuth();
  };

  const handleLogout = () => {
    clearToken();
    setIsAuthenticated(false);
    setUsername("");
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <Router>
      <nav className="navbar">
        {/* Logo */}
        <div className="navbar-brand">
          <span className="navbar-logo">🛍️</span>
          <span className="navbar-title">Shopify Generator</span>
        </div>

        {/* Nav Links */}
        <div className="navbar-links">
          <NavLink to="/" end className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Shopify Converter
          </NavLink>
          <NavLink to="/check-inventory" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Check Inventory
          </NavLink>
          <NavLink to="/variant-inventory" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Variant Generator
          </NavLink>
          <NavLink to="/preprocess" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Preprocess File
          </NavLink>
          <NavLink to="/sku-match" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Shopify SKU Match
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
            Settings
          </NavLink>
        </div>

        {/* User Section */}
        <div className="navbar-user">
          <div className="user-avatar">
            {username ? username.charAt(0).toUpperCase() : "U"}
          </div>
          <span className="user-name">{username}</span>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Logout
          </button>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<ShopifyConverter />} />
        <Route path="/check-inventory" element={<CheckInventory />} />
        <Route path="/variant-inventory" element={<VariantInventoryGenerator />} />
        <Route path="/preprocess" element={<PreprocessFile />} />
        <Route path="/sku-match" element={<ShopifySkuMatch />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
